<template>
  <div :class="['content-width-restrict', detailedMode ? 'mb-3' : fullScreenMode ? '' : 'mb-2']">
    <!-- do not remove below, since this is required to rerender component -->
    <div style="display:none">{{id}}:{{currentId}}</div>

    <div class="row">
      <div class="col-12" v-for="(item, index) in this.items" :key="index">
        <div @click="select($event, item)" :key="item.index" :id="`card_${index}`">
          <Carousel v-if="item.isPromo" :isDisplayed="isDisplayed" :category="item" :ref="`card_${index}`" class="col-12 pr-0 pl-0"/>
          <Asset v-if="item.isVod" :item="item" :ref="`card_${index}`"/>
          <Poll v-if="item.isPoll" :item="item" :ref="`card_${index}`" :isOpenForAnonymous="true"/>
          <Category v-if="item.isSwimlane" :item="item" :ref="`card_${index}`"/>
          <Category v-if="item.isCategory" :item="item" :detailedMode="detailedMode" :rtlOrientation="index % 2 === 0" :ref="`card_${index}`"/>
          <Banner v-if="item.isBanner" :item="item"/>
          <TeamSelectorCard v-if="item.isTeamSelector" :item="item" :ref="`card_${index}`"/>
          <Article v-if="item.isArticle" :item="item" :ref="`card_${index}`"/>
        </div>
      </div>
    </div>

    <div id="networkError" v-show="networkError">
      <div class="network-error d-flex flex-column align-items-center">
        <span>Please check your network connection and try again.</span>
        <button class="primary ml-0 mt-1 mb-0" @click="resetInfiniteScroll">Retry</button>
      </div>
    </div>

    <div id="noData" v-show="noData">
      <div class="no-data d-flex flex-column align-items-center mt-3">
        <span>Category is empty</span>
      </div>
    </div>

    <infinite-loading :identifier="infiniteLoadingIdentifier" @infinite="loadMore">
      <div slot="spinner" />
      <div slot="no-more" />
      <div slot="no-results" />
      <div slot="error" />
    </infinite-loading>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { spinnakerApi } from '../js/SpinnakerApi.js'
import Article from '../views/Article'
import Asset from '../views/Asset.vue'
import Poll from '../views/SimplePoll.vue'
import InfiniteLoading from 'vue-infinite-loading'
import Carousel from './Carousel.vue'
import Category from './Category.vue'
import Banner from './Banner.vue'
import TeamSelectorCard from '../views/TeamSelectorCard.vue'

const MAX_PAGES = 5

export default {
  name: 'More',
  computed: {
    ...mapState({
      isRegisteredUser: state => state.isRegisteredUser
    }),
    fullScreenMode: function () {
      return this.items?.some(item => item.isTeamSelector)
    }
  },
  props: ['id', 'sharedItem', 'isDisplayed', 'detailedMode'],
  watch: {
    isDisplayed: function (newValue, oldValue) {
      if (newValue) {
        this.startUpdateChecks()
      } else {
        this.$store.commit('hideRefreshNotice')
        this.stopUpdateChecks()
      }
    }
  },
  components: {
    InfiniteLoading, Asset, Poll, Carousel, Category, Banner, TeamSelectorCard, Article
  },
  created: async function () {
    console.info('created more cat: ' + this.id)
    this.$store.commit('showLoading')
    if (this.sharedItem && this.sharedItem.progId) {
      this.filterOutSharedItem(this.sharedItem)
    }
    await this.loadMore()
    this.$store.commit('hideLoading')
  },
  beforeDestroy: function () {
    this.stopUpdateChecks()
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  mounted: function () {
    console.info(
      'mounted more cat: ' + this.id + ' isDisplayed: ' + this.isDisplayed
    )
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
        case 'share/setMetadata':
          this.filterOutSharedItem(state.share)
          break
        case 'performRefresh':
          this.refresh()
          break
        case 'declineRefresh':
          this.declineRefresh()
          break
      }
    })

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  updated: function () {
    if (this.currentId !== this.id) {
      console.info(`More.updated: newId:${this.id} oldId:${this.currentId}`)
      this.currentId = this.id
      this.$store.commit('showLoading')
      this.refresh()
    }
  },
  data: function () {
    return {
      items: [],
      selected: [],
      page: 0,
      allPages: null,
      currentId: null,
      networkError: false,
      noData: false,
      infiniteLoadingIdentifier: 0,
      updateCheckIntervalTimer: null,
      updateCheckTimeoutTimer: null,
      updateCheckSnoozeUntil: null,
      unsubscribe: null,
      loadingMoreItems: false
    }
  },
  methods: {
    loadItems: async function () {
      const response = await spinnakerApi.getCategoryItems(
        this.id,
        this.sharedItem,
        this.page
      )

      if (response.items) {
        response.items.forEach((item) => { item.categoryId = this.id })
      }
      return response
    },
    loadMore: async function ($state) {
      this.currentId = this.id
      if (this.allPages && this.page >= this.allPages) {
        console.info(`More.loadMore: complete id:${this.currentId} page:${this.page} allPages:${this.allPages}`)
        $state && $state.complete()
        return
      }
      if (this.loadingMoreItems) {
        return
      } else {
        this.loadingMoreItems = true
      }
      console.info(`More.loadMore: loading id:${this.currentId} page:${this.page} allPages:${this.allPages}`)

      this.$store.commit('showLoading')

      this.networkError = false
      this.noData = false

      let loadItemsResponse = null
      try {
        loadItemsResponse = await this.loadItems()
      } catch (err) {
        console.error(`Error loading category items: ${err}`)
        $state && $state.error()
        this.networkError = true
      }

      if (loadItemsResponse && loadItemsResponse.totalPages > 0) {
        if (this.page === 0) {
          this.startUpdateChecks()
        }
        this.page++
        this.allPages = Math.min(MAX_PAGES, loadItemsResponse.totalPages)
        this.items = this.items.concat(loadItemsResponse.items)
        this.noData = this.items.length === 0
        $state && $state.loaded()
      } else {
        $state && $state.error()
        this.networkError = true
      }

      if (this.networkError) {
        setTimeout(() => {
          document
            .getElementById('networkError')
            .scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        }, 1)
      }
      if (this.noData) {
        setTimeout(() => window.scrollTo(0, 0), 1)
        $state && $state.reset()
      }

      this.loadingMoreItems = false
      this.$store.commit('hideLoading')
    },
    startUpdateChecks: function () {
      this.stopUpdateChecks()
      console.info(`Starting update checks for content list ${this.currentId}`)

      // we set a long-running interval timer but also a quick check sooner
      // in order to handle getting a stale response from service worker using
      // StaleWhileInvalidate on first entry or when coming back from background
      this.updateCheckIntervalTimer = setInterval(this.checkForUpdates, 30 * 1000)
      this.updateCheckTimeoutTimer = setTimeout(this.checkForUpdates, 5 * 1000)
      this.updateCheckSnoozeUntil = null
    },
    stopUpdateChecks: function () {
      if (this.updateCheckIntervalTimer || this.updateCheckTimeoutTimer) {
        console.info(`Stopping update checks for content list ${this.currentId}`)

        clearInterval(this.updateCheckIntervalTimer)
        clearTimeout(this.updateCheckTimeoutTimer)
        this.updateCheckIntervalTimer = null
        this.updateCheckTimeoutTimer = null
      }
    },
    snoozeUpdateChecks: function () {
      this.updateCheckSnoozeUntil = Date.now() + (5 * 60 * 1000)
    },
    checkForUpdates: async function () {
      if (!this.isDisplayed || !this.items || this.items.length === 0) {
        return
      }

      if (this.updateCheckSnoozeUntil && Date.now() < this.updateCheckSnoozeUntil) {
        console.info(`checkForUpdates: snoozing`)
        return
      }

      const isDirty = await this.isUpdateAvailable()
      console.info(`checkForUpdates: isDirty? ${isDirty}`)

      if (isDirty) {
        this.$store.commit('showRefreshNotice')
      }
    },
    isUpdateAvailable: async function () {
      let latestItems = null
      try {
        // Note 1: fetching only category.json (no programme.json for every asset)
        //         it is enough to detect an update
        // Note 2: we are looking for changes only on 1st page, which can be not enough,
        //         but is fair enough
        const response = await spinnakerApi.getCategory(this.id, 0)
        latestItems = response.items
      } catch (err) {
        console.info(`isUpdateAvailable: failed with: ${err}`)
        return false
      }

      // First check - received item list is bigger than what we have right now
      let isDirty = this.items.length < latestItems.length
      if (!isDirty) {
        // Second check - looking for any asset changes (including order)
        isDirty = latestItems.some((latestItem, latestItemIndex) => {
          const latestItemId = spinnakerApi.extractGuid(latestItem.link)

          const correspondingCurrentItemId = this.items[latestItemIndex].isVod
            ? this.items[latestItemIndex].progId
            : this.items[latestItemIndex].id

          return latestItemId !== correspondingCurrentItemId
        })
      }

      return isDirty
    },
    filterOutSharedItem (share) {
      if (this.items) {
        const shareItemIndex = this.items.findIndex(
          i => i.progId === share.progId
        )
        if (shareItemIndex > -1) {
          this.items.splice(shareItemIndex, 1)
        }
      }
    },
    select (e, item) {
      if (e && e.currentTarget) {
        const selectedIndex = this.selected.indexOf(e.currentTarget.id)
        if (selectedIndex > -1) {
          this.selected.splice(selectedIndex, 1)
        } else {
          this.selected.push(e.currentTarget.id)
          document
            .getElementById(e.currentTarget.id)
            .scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    },
    refresh () {
      if (!this.isDisplayed) {
        return
      }
      this.items = []
      this.page = 0
      this.resetInfiniteScroll()
    },
    resetInfiniteScroll () {
      // according to docs, changing infinite loading identifier resets it and triggers items fetch
      // see https://peachscript.github.io/vue-infinite-loading/guide/use-with-filter-or-tabs.html
      this.infiniteLoadingIdentifier++
    },
    declineRefresh () {
      this.snoozeUpdateChecks()
    },
    async handleVisibilityChange () {
      console.info(`handleVisibilityChange ${document.visibilityState} for content list ${this.currentId} ` +
        `isDisplayed=${this.isDisplayed}`)
      if (!this.isDisplayed || document.visibilityState !== 'visible') {
        return
      }

      console.info(`handleVisibilityChange - checking for updates`)

      // restart update check timer (a) so that it doesn't fire while we're working and (b) it is possible that an
      // update is available but that service worker's StaleWhileRevalidate strategy is serving stale data while
      // fetching latest - startUpdateChecks will check in a few seconds, giving that process a chance to complete
      this.stopUpdateChecks()

      const isDirty = await this.isUpdateAvailable()
      if (isDirty) {
        this.$store.commit('showRefreshNotice')
      }

      this.startUpdateChecks()
    }
  }
}
</script>
